// ============================================================
//  src/components/UI.jsx
//  Reusable low-level UI primitives
// ============================================================

import { useEffect } from "react";
import config from "../config";

const { colors } = config;

// ── Button ───────────────────────────────────────────────────
export function Button({ children, onClick, variant = "primary", fullWidth = false, style = {} }) {
  const base = {
    border: "none",
    borderRadius: 13,
    padding: "14px 20px",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    width: fullWidth ? "100%" : "auto",
    transition: "opacity 0.15s",
    ...style,
  };

  const variants = {
    primary:   { background: colors.primary,   color: "#fff" },
    accent:    { background: colors.accent,     color: "#fff" },
    outline:   { background: "#fff", color: colors.primary, border: `2px solid ${colors.primary}` },
    whatsapp:  { background: "#25D366",         color: "#fff" },
    ghost:     { background: colors.background, color: colors.primary, border: `2px solid ${colors.cardBorder}` },
  };

  return (
    <button className="btn-press" style={{ ...base, ...variants[variant] }} onClick={onClick}>
      {children}
    </button>
  );
}

// ── Input ────────────────────────────────────────────────────
export function Input({ label, type = "text", placeholder, value, onChange, multiline = false }) {
  const inputStyle = {
    width: "100%",
    border: `2px solid ${colors.cardBorder}`,
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 15,
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginBottom: 12,
    background: colors.background,
    resize: multiline ? "vertical" : undefined,
  };

  return (
    <div>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 800, color: colors.textMuted, marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </label>
      )}
      {multiline ? (
        <textarea style={{ ...inputStyle, minHeight: 90 }} placeholder={placeholder} value={value} onChange={onChange} />
      ) : (
        <input style={inputStyle} type={type} placeholder={placeholder} value={value} onChange={onChange} />
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────
export function Card({ children, style = {}, hoverable = false }) {
  return (
    <div
      className={hoverable ? "card-hover" : ""}
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: "16px 14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        border: `1px solid ${colors.cardBorder}`,
        marginBottom: 14,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Badge / Tag ───────────────────────────────────────────────
export function Badge({ children, color = colors.primary, bg }) {
  return (
    <span style={{
      display: "inline-block",
      background: bg || `${color}18`,
      color,
      fontSize: 11,
      fontWeight: 700,
      padding: "4px 11px",
      borderRadius: 20,
      marginRight: 6,
    }}>
      {children}
    </span>
  );
}

// ── Toast notification ────────────────────────────────────────
export function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fade-in" style={{
      position: "fixed", bottom: 88, left: "50%", transform: "translateX(-50%)",
      background: colors.primary, color: "#fff",
      padding: "11px 22px", borderRadius: 30,
      fontWeight: 700, zIndex: 9999,
      boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
      fontSize: 13, whiteSpace: "nowrap",
      maxWidth: "88vw", textAlign: "center",
    }}>
      ✅ {message}
    </div>
  );
}

// ── Section title ─────────────────────────────────────────────
export function SectionTitle({ children }) {
  return (
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 800, color: colors.text }}>
      {children}
    </div>
  );
}

// ── Error message ─────────────────────────────────────────────
export function ErrorMessage({ message }) {
  if (!message) return null;
  return (
    <div style={{ color: "#e74c3c", fontSize: 13, marginBottom: 12, background: "#fdecea", padding: "10px 13px", borderRadius: 10 }}>
      ⚠️ {message}
    </div>
  );
}
